import React from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';

export type RerunDialogProps = {
  open: boolean;
  customerName: string;
  weekDisplay: string;
  /** Null when no upload has been recorded for the week — the phrase is then omitted. */
  fileSummary: string | null;
  running: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * The customer leads, because it is the thing that can actually be wrong: the selected customer
 * persists across sessions, while the week is already pinned to the card the button sits on.
 */
export const RerunDialog = ({
  open,
  customerName,
  weekDisplay,
  fileSummary,
  running,
  onCancel,
  onConfirm,
}: RerunDialogProps) => (
  <Dialog open={open} onClose={running ? undefined : onCancel} maxWidth="sm" fullWidth>
    <DialogTitle>Rerun this week?</DialogTitle>
    <DialogContent>
      <Alert severity="info" icon={false} sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 19 }}>{customerName}</Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Week of {weekDisplay}
          {fileSummary ? ` · ${fileSummary}` : ''}
        </Typography>
      </Alert>
      <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
        Replicates the scheduled job &mdash; picks up every file uploaded for this week,
        regenerates the output files, and sends the usual emails. Overwrites anything generated
        by the previous run.
      </Typography>
    </DialogContent>
    <Divider />
    <DialogActions>
      <Button onClick={onCancel} color="inherit" disabled={running}>
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        disableElevation
        disabled={running}
        startIcon={running ? <CircularProgress size={16} color="inherit" /> : null}
        // Matches the button that opened it, so the weight carries through to the commit.
        sx={{
          fontWeight: 600,
          // Emphatic here, unlike the quiet entry point: by this screen the choice is being
          // made, and the commit should look like one.
          backgroundColor: 'error.dark',
          color: 'common.white',
          '&:hover': { backgroundColor: '#8e1f1f' },
        }}
      >
        {running ? 'Running…' : 'Rerun now'}
      </Button>
    </DialogActions>
  </Dialog>
);
